import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2 } from 'lucide-react';
import api from '../services/api';
import { useToast } from '../components/providers/ToastProvider';
import Sidebar from "../components/layout/Sidebar";
import Header from "../components/layout/Header";

interface SubscriptionPlan {
  id: number;
  name: string;
  price: number | null;
  icon_color: string | null;
}

export default function AdminSubscriptions() {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  const [formData, setFormData] = useState({ name: '', price: '', icon_color: '' });
  const { showToast } = useToast();

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const { data } = await api.get('/admin/subscription-plans');
      setPlans(data);
    } catch (err) {
      showToast('Failed to fetch subscription plans', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (plan?: SubscriptionPlan) => {
    if (plan) {
      setEditingPlan(plan);
      setFormData({
        name: plan.name,
        price: plan.price ? plan.price.toString() : '',
        icon_color: plan.icon_color || '',
      });
    } else {
      setEditingPlan(null);
      setFormData({ name: '', price: '', icon_color: '' });
    }
    setShowModal(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        price: formData.price ? parseFloat(formData.price) : null,
        icon_color: formData.icon_color || null,
      };

      if (editingPlan) {
        await api.patch(`/admin/subscription-plans/${editingPlan.id}`, payload);
        showToast('Plan updated successfully', 'success');
      } else {
        await api.post('/admin/subscription-plans', payload);
        showToast('Plan created successfully', 'success');
      }
      setShowModal(false);
      fetchPlans();
    } catch (err) {
      showToast('Failed to save plan', 'error');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this plan?')) return;
    try {
      await api.delete(`/admin/subscription-plans/${id}`);
      showToast('Plan deleted successfully', 'success');
      fetchPlans();
    } catch (err) {
      showToast('Failed to delete plan', 'error');
    }
  };

  return (
    <div className="min-h-screen bg-background text-on-background">
      <Header />
      <div className="flex pt-16">
        <Sidebar />
        <main className="flex-1 w-full md:pl-20 transition-all duration-300">
          <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6 sm:space-y-8">
            
            {/* Header Section */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-surface-container-low p-6 rounded-[2rem] border border-outline-variant/10 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl pointer-events-none" />
              <div className="relative z-10">
                <h1 className="text-2xl sm:text-3xl font-black text-on-surface tracking-tighter">Subscription Plans</h1>
                <p className="text-sm font-medium text-slate-500 mt-1">Manage membership tiers and standalone bundles.</p>
              </div>
              <button
                onClick={() => handleOpenModal()}
                className="relative z-10 flex items-center gap-2 bg-primary hover:bg-primary-variant text-white px-6 py-3 rounded-full text-sm font-bold shadow-lg shadow-primary/20 transition-all hover:scale-105 active:scale-95"
              >
                <Plus className="h-4 w-4" />
                <span>Add Plan</span>
              </button>
            </div>

      {loading ? (
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map(i => <div key={i} className="h-16 bg-surface-container-low rounded-[1.5rem]"></div>)}
        </div>
      ) : (
        <div className="bg-surface-container-low rounded-[1.5rem] sm:rounded-[2rem] shadow-sm border border-outline-variant/10 overflow-hidden">
          <table className="w-full text-left text-sm text-on-surface-variant">
            <thead className="bg-surface-container-highest/20 text-on-surface-variant/70 border-b border-outline-variant/10">
              <tr>
                <th className="px-6 py-4 font-black uppercase tracking-widest text-[10px]">ID</th>
                <th className="px-6 py-4 font-black uppercase tracking-widest text-[10px]">Plan Name</th>
                <th className="px-6 py-4 font-black uppercase tracking-widest text-[10px]">Price</th>
                <th className="px-6 py-4 font-black uppercase tracking-widest text-[10px] text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/10">
              {plans.map(plan => (
                <tr key={plan.id} className="hover:bg-surface-container-highest/30 transition-colors">
                  <td className="px-6 py-4 font-medium text-slate-500">{plan.id}</td>
                  <td className="px-6 py-4 font-black text-on-surface flex items-center space-x-3">
                    {plan.icon_color && (
                      <span className="w-3 h-3 rounded-full shadow-sm" style={{ backgroundColor: plan.icon_color }}></span>
                    )}
                    <span>{plan.name}</span>
                  </td>
                  <td className="px-6 py-4 font-bold text-primary">{plan.price ? `₹${plan.price}` : 'Free'}</td>
                  <td className="px-6 py-4 text-right space-x-3">
                    <button onClick={() => handleOpenModal(plan)} className="text-slate-400 hover:text-blue-500 transition-colors">
                      <Edit2 className="h-4 w-4 inline" />
                    </button>
                    <button onClick={() => handleDelete(plan.id)} className="text-slate-400 hover:text-red-500 transition-colors">
                      <Trash2 className="h-4 w-4 inline" />
                    </button>
                  </td>
                </tr>
              ))}
              {plans.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-6 py-12 text-center text-slate-500 font-medium">No plans found. Create one to get started.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-surface-container-lowest rounded-[2rem] shadow-2xl w-full max-w-md overflow-hidden border border-outline-variant/10 animate-in zoom-in-95 duration-200">
            <div className="px-6 py-5 border-b border-outline-variant/10 flex justify-between items-center bg-surface-container-low/50">
              <h3 className="text-xl font-black text-on-surface tracking-tight">
                {editingPlan ? 'Edit Subscription Plan' : 'Create Subscription Plan'}
              </h3>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-on-surface transition-colors">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            
            <form onSubmit={handleSave} className="p-6 space-y-5">
              <div>
                <label className="block text-[10px] font-black uppercase text-slate-400 tracking-widest pl-2 mb-1">Plan Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-5 py-3.5 bg-surface-container-high text-on-surface border border-outline-variant/10 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none transition-all font-bold text-sm"
                  placeholder="e.g. VIP or TikTok Bundle"
                />
              </div>
              
              <div>
                <label className="block text-[10px] font-black uppercase text-slate-400 tracking-widest pl-2 mb-1">Price (Optional)</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={e => setFormData({ ...formData, price: e.target.value })}
                  className="w-full px-5 py-3.5 bg-surface-container-high text-on-surface border border-outline-variant/10 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none transition-all font-bold text-sm"
                  placeholder="e.g. 1999"
                />
              </div>

              <div>
                <label className="block text-[10px] font-black uppercase text-slate-400 tracking-widest pl-2 mb-1">Brand Color Hex (Optional)</label>
                <div className="flex gap-3">
                  <input
                    type="color"
                    value={formData.icon_color || '#3b82f6'}
                    onChange={e => setFormData({ ...formData, icon_color: e.target.value })}
                    className="w-12 h-12 rounded-xl cursor-pointer bg-transparent border-0 p-0 shrink-0"
                  />
                  <input
                    type="text"
                    value={formData.icon_color}
                    onChange={e => setFormData({ ...formData, icon_color: e.target.value })}
                    className="w-full px-5 py-3.5 bg-surface-container-high text-on-surface border border-outline-variant/10 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none transition-all font-bold text-sm"
                    placeholder="e.g. #3b82f6"
                  />
                </div>
              </div>

              <div className="pt-4 flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-5 py-3.5 bg-surface-container-high hover:bg-surface-container-highest text-on-surface rounded-xl transition-colors font-bold text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-5 py-3.5 bg-primary hover:bg-primary-variant text-white rounded-xl transition-all shadow-lg shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] font-bold text-sm"
                >
                  Save Plan
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
          </div>
        </main>
      </div>
    </div>
  );
}
